import { faPrint } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Button, IconButton, Paper, Typography, useMediaQuery } from '@mui/material';
import { TranscricaoDetalhe } from '../../../../../interfaces/entities/Transcricao/TranscricaoDetalhe';
import { useAccessibility } from '../../../../../Store/contexts/AccessibilityContext';
import { MOBILE_BREAKPOINT } from '../../transcricaoAnexoUtils';
import { sxTranscricaoListaCardDetalhesButton } from '../sxProps';
import TranscricaoSituacaoBadge from '../TranscricaoSituacaoBadge';
import { getTranscricaoStatusConfig } from '../../transcricaoStatusUtils';
import {
  formatTranscricaoDataCompleta,
  getTipoTranscricaoLabel,
} from '../../utils/transcricaoDetalheUtils';
import { sxTranscricaoResumoCard, sxTranscricaoResumoCardMobile } from './sxProps';

interface Props {
  detalhe: TranscricaoDetalhe;
  codStatusTranscricao?: string | null;
  onVisualizarTermo?: () => void;
}

const RESUMO_CAMPOS = [
  { key: 'data', label: 'Data de solicitação' },
  { key: 'situacao', label: 'Situação' },
  { key: 'tipo', label: 'Tipo' },
  { key: 'protocolo', label: 'Protocolo' },
  { key: 'termo', label: 'Termo' },
] as const;

const TranscricaoResumoCard: React.FC<Props> = ({
  detalhe,
  codStatusTranscricao,
  onVisualizarTermo,
}) => {
  const accessibility = useAccessibility();
  const isMobileLayout = useMediaQuery(MOBILE_BREAKPOINT);
  const status = getTranscricaoStatusConfig(
    codStatusTranscricao ?? detalhe.codStatusTranscricao,
    detalhe.descrStatusTranscricao,
  );

  const protocolo = detalhe.codProtocoloans?.trim() || '—';
  const dataSolicitacao = formatTranscricaoDataCompleta(detalhe.dataCadastro);
  const tipo = getTipoTranscricaoLabel(detalhe.codTipoTranscricao);

  if (isMobileLayout) {
    return (
      <Paper
        className="transcricao-detalhe-resumo-card transcricao-detalhe-resumo-card--mobile"
        sx={sxTranscricaoResumoCardMobile}
        style={accessibility.contrastBorderAccessibility}
      >
        <div className="transcricao-detalhe-resumo-mobile-header">
          <div className="transcricao-detalhe-resumo-mobile-field">
            <Typography
              component="span"
              style={accessibility.textStyleAccessibility}
              className="transcricao-detalhe-resumo-mobile-label"
            >
              Protocolo
            </Typography>
            <Typography
              component="span"
              style={accessibility.textStyleAccessibility}
              className="transcricao-detalhe-resumo-mobile-valor"
            >
              {protocolo}
            </Typography>
          </div>
          <TranscricaoSituacaoBadge status={status} />
        </div>

        <div className="transcricao-detalhe-resumo-mobile-field">
          <Typography
            component="span"
            style={accessibility.textStyleAccessibility}
            className="transcricao-detalhe-resumo-mobile-label"
          >
            Data de solicitação
          </Typography>
          <Typography
            component="span"
            style={accessibility.textStyleAccessibility}
            className="transcricao-detalhe-resumo-mobile-valor"
          >
            {dataSolicitacao}
          </Typography>
        </div>

        <div className="transcricao-detalhe-resumo-mobile-field">
          <Typography
            component="span"
            style={accessibility.textStyleAccessibility}
            className="transcricao-detalhe-resumo-mobile-label"
          >
            Tipo
          </Typography>
          <Typography
            component="span"
            style={accessibility.textStyleAccessibility}
            className="transcricao-detalhe-resumo-mobile-valor"
          >
            {tipo}
          </Typography>
        </div>

        <hr className="transcricao-detalhe-resumo-mobile-divider" aria-hidden="true" />

        <Button
          type="button"
          variant="outlined"
          className="transcricao-detalhe-resumo-mobile-termo"
          sx={sxTranscricaoListaCardDetalhesButton}
          style={accessibility.buttonStyleAccessibility}
          onClick={onVisualizarTermo}
          disabled={!detalhe.dataAceite}
          aria-label="Visualizar termo de uso"
          startIcon={
            <FontAwesomeIcon
              icon={faPrint as IconProp}
              style={accessibility.buttonTextStyleAccessibility}
              aria-hidden="true"
            />
          }
        >
          <b style={accessibility.buttonTextStyleAccessibility} aria-hidden="true">
            TERMO
          </b>
        </Button>
      </Paper>
    );
  }

  const valores: Record<(typeof RESUMO_CAMPOS)[number]['key'], React.ReactNode> = {
    data: dataSolicitacao,
    situacao: <TranscricaoSituacaoBadge status={status} />,
    tipo,
    protocolo,
    termo: (
      <IconButton
        className="transcricao-detalhe-termo-button"
        onClick={onVisualizarTermo}
        aria-label="Visualizar termo de uso"
        disabled={!detalhe.dataAceite}
      >
        <FontAwesomeIcon icon={faPrint as IconProp} className="transcricao-detalhe-termo-icon" />
      </IconButton>
    ),
  };

  return (
    <Paper
      className="transcricao-detalhe-resumo-card"
      sx={sxTranscricaoResumoCard}
      style={accessibility.contrastBorderAccessibility}
    >
      {RESUMO_CAMPOS.map((campo) => (
        <div key={campo.key} className="transcricao-detalhe-resumo-item">
          <Typography
            component="span"
            style={accessibility.textStyleAccessibility}
            className="transcricao-detalhe-resumo-label"
          >
            {campo.label}
          </Typography>
          {typeof valores[campo.key] === 'string' ? (
            <Typography
              component="span"
              style={accessibility.textStyleAccessibility}
              className="transcricao-detalhe-resumo-valor"
            >
              {valores[campo.key]}
            </Typography>
          ) : (
            <span className="transcricao-detalhe-resumo-valor">{valores[campo.key]}</span>
          )}
        </div>
      ))}
    </Paper>
  );
};

export default TranscricaoResumoCard;
